// @flow

import R from "ramda";
import { extendObservable, action, runInAction, computed, observable } from "mobx";
import axios from "axios";
import moment from "moment";
import SavedSearch from "../../models/SavedSearch";
import RateCard from "../../models/RateCard";
import CurrentUser from "../../models/User";
import LocationFilter, {
  LOCATION_FILTER_TYPE,
} from "../../models/FilterState/LocationFilter";
import IndustryFilter from "../../models/FilterState/IndustryFilter";
import {
  RateTypeFilter,
  JobLabelFilter,
  JobTitleFilter,
  RateCardLabelFilter,
  DraftFilter,
  CreatedOnFilter,
  JobFilter,
} from "../../models/FilterState";
import MessageState from "../../models/MessageState";
import NetworkState from "../../models/NetworkState";
import PaginationState from "../../models/PaginationState";
import FilterObject, { FILTER_COLUMN } from "../../models/Filter";
import Sort, { SORT_DIRECTION } from "../../models/Sort";
import ModalState from "../../models/ModalState";
import {
  addIdToPayload,
  consolidateAppliedFilters,
  consolidateAppliedSorts,
} from "./SupportFunctions";
import type { PageQuery, PaginationInfo } from "../../models/PaginationState";
import type { FilterColumn } from "../../models/Filter";
import type { GraphQLQuery } from "../../models/GraphQL";
import ExportOptionsState from "../../models/ExportOptionsState";
import ExportOptions, { FILE_TYPE, EXPORT_TYPE } from "../../models/ExportOptions";
import { browserHistory } from "react-router";
import { validateUserName } from "../../utils/validation";
import type { FetchGraphQL, FetchAPI } from "../../App";
import { sanitizeInput } from "../../utils/dom";

const rateTypesCriteriaQuery = `
query {
  viewer {
    rateCardsFilterCriteria {
      rateTypes
    }
  }
}
`;

const jobTitlesFilterCriteriaQuery = `
query{
  viewer{
    savedsearches{
      searchFilterCriteria{
        jobTitles {
          jobTitle
        }
      }
    }
  }
}
`;

const locationsFilterCriteriaQuery = `
query{
  viewer{
    savedsearches{
      searchFilterCriteria{
        countries {
          country
        }
        cities {
          city
        }
        states {
          state
        }
        regions {
          region
        }
      }
    }
  }
}
`;

const jobLabelsFilterCriteriaQuery = `
query{
  viewer{
    savedsearches{
      searchFilterCriteria{
        jobLabels {
          jobLabel
        }
      }
    }
  }
}
`;

const industriesFilterCriteriaQuery = `
query{
  viewer{
    savedsearches{
      searchFilterCriteria{
        industries {
          legacyId
          name
        }
      }
    }
  }
}
`;

// type Owner = { userId: string, username: string };

export class DraftsSavedSearchListComponentStore {
  router: Object;
  rateCardLabel: string;
  network: NetworkState;
  messaging: MessageState;
  networkSavedSearchDetail: NetworkState;
  pagination: PaginationState;

  rateCards: SavedSearch[];
  savedSearchCount: any;
  allRateCards: RateCard[];
  rateCardsViewState: Object;
  rateCardsView: SavedSearch[];

  currentUser: CurrentUser;
  isEditing: ?boolean;
  allOnPageSelected: boolean;
  allSelected: boolean;
  allSelectedfilter: boolean;
  allowMultipleItemSelection: boolean;

  industryFilter: IndustryFilter;
  jobLabelFilter: JobLabelFilter;
  rateTypeFilter: RateTypeFilter;
  jobFilter: JobTitleFilter;
  rateCardLabelFilter: RateCardLabelFilter;
  locationFilter: LocationFilter;
  draftFilter: DraftFilter;
  createdOnFilter: CreatedOnFilter;

  defaultFilters: {
    [key: FilterColumn]: FilterObject,
  };
  appliedFilters: {
    [key: FilterColumn]: FilterObject,
  };
  appliedSorts: {
    [key: FilterColumn]: Sort,
  };
  appliedSortsOrder: Array<FilterColumn>;
  unSelectedSearches: [];
  isFiltered: boolean;
  addSearchesModal: ModalState;
  jobSearchFilter: JobFilter;

  getSavedSearch: (PageQuery) => Promise<PaginationInfo>;
  getAllRateCardsList: () => void;
  savedSearchDetail: () => void;
  applyDefaultFilter: (FilterColumn, FilterObject) => void;
  handleStartEdit: () => void;
  handleStopEdit: () => void;
  toggleSelectAllPage: (Object) => void;
  selectAllPage: (Event) => void;
  deselectAllPage: (Event) => void;
  toggleAllItems: () => void;
  clearAllSelections: () => void;
  getSelectedRateCards: () => Array<string>;
  getFirstSelectedRateCard: () => Object;
  toDateRange: () => void;
  getFilterCriteriaQuery: (FilterColumn) => GraphQLQuery;
  processFilterCriteria: (FilterColumn, Object) => Array<Object>;
  applyFilter: (FilterColumn, FilterObject) => void;
  removeFilter: (FilterColumn) => void;
  applySort: (FilterColumn, Sort) => void;
  removeSort: (FilterColumn) => void;
  filterRateCards: () => void;
  clearFilters: () => void;
  applyDefaultSort: () => void;
  deSelectAll: () => void;
  selectAllOnPageItem: () => void;
  fetchGraphQL: FetchGraphQL;

  constructor(fetchGraphQL: FetchGraphQL) {
    this.fetchGraphQL = fetchGraphQL;
    this.router = null;

    // NOTE: Bound early to pass into pagination & filter state
    this.getSavedSearch = action(this.getSavedSearch.bind(this));
    this.getAllRateCardsList = action(this.getAllRateCardsList.bind(this));
    this.savedSearchDetail = action(this.savedSearchDetail.bind(this));
    this.getFilterCriteriaQuery = action(this.getFilterCriteriaQuery.bind(this));
    this.processFilterCriteria = action(this.processFilterCriteria.bind(this));
    this.applyFilter = action(this.applyFilter.bind(this));
    this.applySort = action(this.applySort.bind(this));
    this.removeFilter = action(this.removeFilter.bind(this));
    this.removeSort = action(this.removeSort.bind(this));
    this.toDateRange = action(this.toDateRange.bind(this));

    extendObservable(this, {
      network: new NetworkState(),
      messaging: new MessageState(),
      networkSavedSearchDetail: new NetworkState(),
      pagination: new PaginationState(this.getSavedSearch),
      rateCards: [],
      savedSearchCount: 0,
      allRateCards: [],
      savedsearchdetaildata: [],
      rateCardsViewState: observable.map({}),
      rateCardsView: computed(() => {
        return this.rateCards.map((rateCard) => {
          if (this.rateCardsViewState.has(rateCard.searchId)) {
            rateCard.viewState = this.rateCardsViewState.get(rateCard.searchId);

            return rateCard;
          }

          return rateCard;
        });
      }),
      isEditing: null, // we start with null so some view elements be hidden initially
      isFiltered: false,
      allSelected: false,
      allSelectedfilter: false,
      rateCardLabel: "",
      unSelectedSearches: [],
      allowMultipleItemSelection: true,
      selectedCount: computed(() => {
        const selectedValues = this.rateCardsView.map(
          (rateCard) => rateCard.viewState.selected
        );

        if (this.allSelected) {
          return this.pagination.totalCount;
        }

        let count = 0;

        selectedValues.forEach((v) => {
          if (v) {
            count += 1;
          }
        });

        return count;
      }),
      allOnPageSelected: computed(() => {
        const allTrue = R.all(R.equals(true));
        const selectedValues = this.rateCardsView.map(
          (rateCard) => rateCard.viewState.selected
        );

        if (selectedValues.length === 0) {
          return false;
        }

        return allTrue(selectedValues);
      }),
      defaultFilters: {},
      appliedFilters: {},
      appliedSorts: {},
      addSearchesModal: new ModalState(),
      appliedSortsOrder: observable.shallow([]),

      rateTypeFilter: new RateTypeFilter(
        this,
        FILTER_COLUMN.RATE_TYPE,
        this.getFilterCriteriaQuery,
        this.processFilterCriteria,
        this.applyFilter,
        this.removeFilter
      ),
      jobLabelFilter: new JobLabelFilter(
        this,
        FILTER_COLUMN.JOB_LABEL,
        this.getFilterCriteriaQuery,
        this.processFilterCriteria,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
      jobFilter: new JobTitleFilter(
        this,
        FILTER_COLUMN.JOB_TITLE,
        this.getFilterCriteriaQuery,
        this.processFilterCriteria,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
      rateCardLabelFilter: new RateCardLabelFilter(
        this,
        FILTER_COLUMN.RATE_CARD_LABEL,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
      locationFilter: new LocationFilter(
        this,
        FILTER_COLUMN.LOCATION,
        this.getFilterCriteriaQuery,
        this.processFilterCriteria,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
      draftFilter: new DraftFilter(
        this,
        FILTER_COLUMN.DRAFT,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
      createdOnFilter: new CreatedOnFilter(
        this,
        FILTER_COLUMN.DATE_RANGE,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
      industryFilter: new IndustryFilter(
        this,
        FILTER_COLUMN.INDUSTRY,
        this.getFilterCriteriaQuery,
        this.processFilterCriteria,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
      jobSearchFilter: new JobFilter(
        this,
        FILTER_COLUMN.JOB_LABEL,
        this.applyFilter,
        this.applySort,
        this.removeFilter,
        this.removeSort
      ),
    });

    this.currentUser = null;

    this.applyDefaultFilter = action(this.applyDefaultFilter.bind(this));
    this.handleStartEdit = action(this.handleStartEdit.bind(this));
    this.handleStopEdit = action(this.handleStopEdit.bind(this));
    this.toggleSelectAllPage = action(this.toggleSelectAllPage.bind(this));
    this.selectAllPage = action(this.selectAllPage.bind(this));
    this.deselectAllPage = action(this.deselectAllPage.bind(this));
    this.toggleAllItems = action(this.toggleAllItems.bind(this));
    this.clearAllSelections = action(this.clearAllSelections.bind(this));
    this.getSelectedRateCards = action(this.getSelectedRateCards.bind(this));
    this.getFirstSelectedRateCard = action(this.getFirstSelectedRateCard.bind(this));
    this.deSelectAll = action(this.deSelectAll.bind(this));

    this.filterRateCards = action(this.filterRateCards.bind(this));
    this.clearFilters = action(this.clearFilters.bind(this));
    this.applyDefaultSort = action(this.applyDefaultSort.bind(this));
    this.selectAllOnPageItem = action(this.selectAllOnPageItem.bind(this));

    this.applyDefaultSort();
  }

  applyDefaultSort() {
    this.createdOnFilter.sortState.direction = SORT_DIRECTION.DESC;
    this.createdOnFilter.sort = this.createdOnFilter.buildQuerySort();
    this.applySort(this.createdOnFilter.column, this.createdOnFilter.sort);
  }

  toDateRange(date) {
    var val = true;
    if (this.createdOnFilter.fromDate) {
      val = !date.isBetween(
        this.createdOnFilter.fromDate,
        moment(new Date()),
        "days",
        "[]"
      );
    }
    return val;
  }

  handleStartEdit() {
    this.isEditing = true;
    this.rateCardsView.forEach((rateCard) => rateCard.toggleEdit());
  }

  handleStopEdit() {
    this.isEditing = false;
    this.allSelected = false;
    this.allSelectedfilter = false;
    this.rateCardsViewState.forEach((viewState) => {
      viewState.selected = false;
      viewState.editing = false;
    });
  }

  deSelectAll() {
    // this.rateCards = [];
    this.allSelected = false;
    this.rateCardsViewState.forEach((viewState) => {
      viewState.selected = false;
      viewState.editing = false;
    });
  }

  toggleSelectAllPage(e: Object) {
    if (!this.allowMultipleItemSelection) return;

    const setValue = !this.allOnPageSelected;

    this.rateCardsView.forEach((rateCard) => {
      rateCard.toggleSelected(e, null, null, setValue);
    });

    // When All items selected flag is up, clear selection
    if (setValue === false && this.allSelected) this.allSelected = false;
  }

  selectAllPage(e: Event) {
    this.unSelectedSearches = [];
    this.allSelected = true;
    this.allSelectedfilter = true;
  }

  selectAllOnPageItem(e: Event) {
    this.rateCardsView.forEach((rateCard) => {
      rateCard.toggleSelected(e, null, null, true);
    });
  }

  deselectAllPage(e: Event) {
    this.rateCardsView.forEach((rateCard) => {
      rateCard.toggleSelected(e, null, null, false);
    });
    this.allSelected = false;
    this.allSelectedfilter = false;
  }

  toggleAllItems() {
    if (!this.allowMultipleItemSelection) return;

    this.allSelected = !this.allSelected;

    if (this.allSelected === false) {
      this.rateCardsViewState.forEach((value) => {
        value.selected = false;
      });
    }
  }

  clearAllSelections() {
    this.allSelected = false;
    this.allSelectedfilter = false;
    this.rateCardsViewState.forEach((value) => {
      value.selected = false;
    });
    ///this.addSearchesModal.hideModal();
  }

  getFilterCriteriaQuery(column: FilterColumn): GraphQLQuery {
    switch (column) {
      case FILTER_COLUMN.RATE_TYPE:
        return {
          query: rateTypesCriteriaQuery,
          variables: {},
        };

      case FILTER_COLUMN.JOB_LABEL:
        return {
          query: jobLabelsFilterCriteriaQuery,
          variables: {},
        };

      case FILTER_COLUMN.JOB_TITLE:
        return {
          query: jobTitlesFilterCriteriaQuery,
          variables: {},
        };

      case FILTER_COLUMN.LOCATION:
        return {
          query: locationsFilterCriteriaQuery,
          variables: {},
        };

      case FILTER_COLUMN.INDUSTRY:
        return {
          query: industriesFilterCriteriaQuery,
          variables: {},
        };

      default:
        return null;
    }
  }

  processFilterCriteria(column: FilterColumn, payload: Object): ?Array<Object> {
    switch (column) {
      case FILTER_COLUMN.RATE_TYPE:
        const rateTypes: [String] = payload.data.viewer.rateCardsFilterCriteria.rateTypes;
        return addIdToPayload(rateTypes);

      case FILTER_COLUMN.JOB_LABEL:
        const jobLabels: [{ jobLabel: String }] =
          payload.data.viewer.savedsearches.searchFilterCriteria.jobLabels;
        return addIdToPayload(jobLabels);

      case FILTER_COLUMN.JOB_TITLE:
        const jobTitles: [{ jobTitle: String }] =
          payload.data.viewer.savedsearches.searchFilterCriteria.jobTitles;
        return addIdToPayload(jobTitles);

      case FILTER_COLUMN.LOCATION:
        const regions: [
          {
            region: String,
          }
        ] = payload.data.viewer.savedsearches.searchFilterCriteria.regions;

        const countries: [
          {
            country: String,
          }
        ] = payload.data.viewer.savedsearches.searchFilterCriteria.countries;

        const states: [
          {
            state: String,
          }
        ] = payload.data.viewer.savedsearches.searchFilterCriteria.states;

        const cities: [
          {
            city: String,
          }
        ] = payload.data.viewer.savedsearches.searchFilterCriteria.cities;

        let processedLocations = observable.map({});
        regions.forEach((item, i) => {
          if (item.region)
            processedLocations.set(LOCATION_FILTER_TYPE.REGION + String(i), {
              id: LOCATION_FILTER_TYPE.REGION + String(i),
              type: LOCATION_FILTER_TYPE.REGION,
              location: item.region,
            });
        });

        countries.forEach((item, i) => {
          if (item.country)
            processedLocations.set(LOCATION_FILTER_TYPE.COUNTRY + String(i), {
              id: LOCATION_FILTER_TYPE.COUNTRY + String(i),
              type: LOCATION_FILTER_TYPE.COUNTRY,
              location: item.country,
            });
        });

        states.forEach((item, i) => {
          if (item.state)
            processedLocations.set(LOCATION_FILTER_TYPE.STATE + String(i), {
              id: LOCATION_FILTER_TYPE.STATE + String(i),
              type: LOCATION_FILTER_TYPE.STATE,
              location: item.state,
            });
        });

        cities.forEach((item, i) => {
          if (item.city)
            processedLocations.set(LOCATION_FILTER_TYPE.CITY + String(i), {
              id: LOCATION_FILTER_TYPE.CITY + String(i),
              type: LOCATION_FILTER_TYPE.CITY,
              location: item.city,
            });
        });

        return processedLocations;

      case FILTER_COLUMN.INDUSTRY:
        const industries: [{ name: String, legacyId: String }] =
          payload.data.viewer.savedsearches.searchFilterCriteria.industries;
        let processedIndustries = observable.map({});
        industries.forEach((industry) => {
          processedIndustries.set(String(industry.legacyId), {
            id: String(industry.legacyId),
            ...industry,
          });
        });

        return processedIndustries;

      case FILTER_COLUMN.CREATED_BY:
        const owners: [
          {
            userId: string,
            username: string,
          }
        ] = payload.data.viewer.authors.edges;
        let processedOwners = observable.map({});
        owners.forEach((owner) => {
          processedOwners.set(String(owner.node.userId), {
            id: String(owner.node.userId),
            ...owner,
          });
        });

        return processedOwners;

      default:
        return null;
    }
  }

  applyFilter(column: FilterColumn, filter: FilterObject) {
    this.appliedFilters[column] = filter;
    this.isFiltered = true;
  }

  applyDefaultFilter(column: FilterColumn, filter: FilterObject) {
    this.defaultFilters[column] = filter;
  }

  removeFilter(column: FilterColumn) {
    delete this.appliedFilters[column];

    let entries = Object.entries(this.appliedFilters);
    if (!entries.length) this.isFiltered = false;
  }

  applySort(column: FilterColumn, sort: Sort) {
    this.appliedSorts[column] = sort;

    const index = this.appliedSortsOrder.indexOf(column);
    if (index === -1) this.appliedSortsOrder.push(column);
  }

  removeSort(column: FilterColumn) {
    delete this.appliedSorts[column];

    const index = this.appliedSortsOrder.indexOf(column);
    if (index > -1) this.appliedSortsOrder.splice(index, 1);
  }

  clearFilters() {
    this.jobSearchFilter = new JobFilter(
      this,
      FILTER_COLUMN.JOB_LABEL,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );
    this.rateTypeFilter = new RateTypeFilter(
      this,
      FILTER_COLUMN.RATE_TYPE,
      this.getFilterCriteriaQuery,
      this.processFilterCriteria,
      this.applyFilter,
      this.removeFilter
    );
    this.jobLabelFilter = new JobLabelFilter(
      this,
      FILTER_COLUMN.JOB_LABEL,
      this.getFilterCriteriaQuery,
      this.processFilterCriteria,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );
    this.jobFilter = new JobTitleFilter(
      this,
      FILTER_COLUMN.JOB_TITLE,
      this.getFilterCriteriaQuery,
      this.processFilterCriteria,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );
    this.rateCardLabelFilter = new RateCardLabelFilter(
      this,
      FILTER_COLUMN.RATE_CARD_LABEL,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );
    this.locationFilter = new LocationFilter(
      this,
      FILTER_COLUMN.LOCATION,
      this.getFilterCriteriaQuery,
      this.processFilterCriteria,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );
    this.draftFilter = new DraftFilter(
      this,
      FILTER_COLUMN.DRAFT,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );
    this.createdOnFilter = new CreatedOnFilter(
      this,
      FILTER_COLUMN.DATE_RANGE,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );
    this.industryFilter = new IndustryFilter(
      this,
      FILTER_COLUMN.INDUSTRY,
      this.getFilterCriteriaQuery,
      this.processFilterCriteria,
      this.applyFilter,
      this.applySort,
      this.removeFilter,
      this.removeSort
    );

    this.appliedFilters = observable({});
    this.appliedSorts = observable({});
    this.appliedSortsOrder.length = 0;
    this.isFiltered = false;
    this.applyDefaultSort();

    return this.pagination.goFetch(null);
  }

  filterRateCards() {
    return this.pagination.goFetch(null);
  }

  getSelectedRateCards(): Array<string> {
    // debugger
    const rateCards = this.rateCardsViewState;

    let selectedRateCards = [];

    rateCards.forEach((value, key) => {
      if (value.selected) {
        selectedRateCards.push(key);
      }
    });
    return selectedRateCards;
  }

  getFirstSelectedRateCard() {
    for (let j = 0; j < this.rateCardsView.length; j++) {
      if (this.rateCards[j].viewState.selected) return this.rateCards[j];
    }

    return null;
  }

  async getAllRateCardsList() {
    let variables;
    let filtersCriteria: string[] = [];

    consolidateAppliedFilters(this.appliedFilters, filtersCriteria, variables);

    // NOTE: applied filters could override default filters, handle this if needed
    consolidateAppliedFilters(this.defaultFilters, filtersCriteria, variables);
    const queryFiltersCriteria = filtersCriteria.join(", ");
    const query = `
    query rateCardList {
        viewer {
          allRateCards(filters: { ${queryFiltersCriteria} }){
            pageInfo {
              hasNextPage
              hasPreviousPage
              startCursor
              endCursor
            }
            totalCount
            edges {
              node {
                ratecardId
                owner {
                  firstName
                  lastName
                  email
                  termsOfAgreement
                }
                name
                createdDate
                updateDate
              }
            }
          }
        }
      }
    `;

    // this.network.loading = true;
    let res = null;

    try {
      res = await this.fetchGraphQL(query, variables);
    } catch (e) {
      if (axios.isCancel(e)) {
        return e;
      }

      // this.network.handleError("Getting Rate Cards", e);
      // if (res !== null) {
      //   this.network.logGraphQLError("Get Rate Card query", res);
      // }

      // TODO: Display user friendly error message
      return e;
    }

    return runInAction("getAllRateCardsList--success", () => {
      // debugger
      // if (this.network.logGraphQLError("Get Rate Cards query", res)) {
      //   // TODO: Display user friendly error message
      //   return { totalCount: 0, startCursor: "", endCursor: "" };
      // }
      // // this.currentUser = new CurrentUser(this, res.data.viewer.user);

      // // $FlowFixMe: Type the res value once we abstract gql calls.
      const cards = res.data.viewer.allRateCards.edges;

      // // TODO: Deserialize this properly...
      this.allRateCards = cards.map((card) => {
        const rateCard = new RateCard(this, card.node);

        // rateCard.searches = card.node.searches.totalCount;
        // rateCard.locations = card.node.locations;

        return rateCard;
      });

      return {
        totalCount: res.data.viewer.allRateCards.totalCount,
        startCursor: res.data.viewer.allRateCards.pageInfo.startCursor,
        endCursor: res.data.viewer.allRateCards.pageInfo.endCursor,
      };
    });
  }

  async getSavedSearch(pageQuery: PageQuery): Promise<PaginationInfo> {
    let params: string[] = pageQuery.params;
    let args = pageQuery.args;
    let variables = pageQuery.variables;
    let filtersCriteria: string[] = [];

    let sortCriteria: string[] = [];
    consolidateAppliedSorts(this.appliedSorts, sortCriteria);

    consolidateAppliedFilters(this.appliedFilters, params, filtersCriteria, variables);

    // NOTE: applied filters could override default filters, handle this if needed
    consolidateAppliedFilters(this.defaultFilters, params, filtersCriteria, variables);

    variables.isDraft = true;

    const queryParams = params.join(", ");
    const queryArgs = args.join(", ");
    const queryFiltersCriteria = filtersCriteria.join(", ");
    const querySortCriteria = sortCriteria.join(", ");

    const query = `
    query savedSearches (${queryParams}) {
        viewer {
          savedsearches(${queryArgs}, filters: { isDraft: true, ${queryFiltersCriteria} }, order: [${querySortCriteria}]) {
            edges {
              node {
                searchId
                job {
                  jobLabel
                  jobTitle
                  jobDescription
                }
                jobDescription
                currency{
                  symbol
                }
                rateType
                city
                country
                state
                marketRate {
                  payRate
                  billRate
                  markupPct
                }
                rates {
                  markupPct {
                    min
                    max
                    avg
                    mid
                  }
                  billRate {
                    min
                    max
                    avg
                    mid
                  }
                  markupAmt {
                    min
                    max
                    avg
                    mid
                  }
                  payRate {
                    min
                    max
                    avg
                    mid
                  }
                }
                createdDate
              }
            }
            totalCount
          }
        }
      }
    `;

    this.network.loading = true;
    let res = null;

    try {
      res = await this.fetchGraphQL(query, variables);
    } catch (e) {
      if (axios.isCancel(e)) {
        return e;
      }

      this.network.handleError("Getting Rate Cards", e);
      if (res !== null) {
        this.network.logGraphQLError("Get Rate Card query", res);
      }

      // TODO: Display user friendly error message
      return e;
    }

    return runInAction("getSavedSearch--success", () => {
      this.network.loading = false;
      this.network.error = null;
      if (this.network.logGraphQLError("Get Rate Cards query", res)) {
        // TODO: Display user friendly error message
        return {
          totalCount: 0,
        };
      }
      // this.currentUser = new CurrentUser(this, res.data.viewer.user);

      // // $FlowFixMe: Type the res value once we abstract gql calls.
      const cards = res.data.viewer.savedsearches.edges;

      this.savedSearchCount = res.data.viewer.savedsearches.totalCount;
      // // TODO: Deserialize this properly...
      this.rateCards = cards.map((card) => {
        const rateCard = new SavedSearch(this, card.node);
        // rateCard.searches = card.node.searches.totalCount;
        // rateCard.locations = card.node.locations;
        if (!this.rateCardsViewState.has(rateCard.searchId)) {
          this.rateCardsViewState.set(rateCard.searchId, {
            selected: this.allSelected,
            editing: this.isEditing,
          });
        } else {
          const selectedValue = this.allSelected
            ? true
            : this.rateCardsViewState.get(rateCard.searchId).selected;

          this.rateCardsViewState.set(rateCard.searchId, {
            selected: selectedValue,
            editing: this.isEditing,
          });
        }

        rateCard.viewState = this.rateCardsViewState.get(rateCard.searchId);

        return rateCard;
      });

      return {
        totalCount: res.data.viewer.savedsearches.totalCount,
      };
    });
  }

  async savedSearchDetail(searchId) {
    let variables = {
      searchId: searchId,
    };

    const query = `
        query saveSearchDetail ($searchId: Int!){
  viewer {
    savedsearch(id: $searchId) {
      searchId
      rateType
          currency {
            symbol
          }
          job {
            jobLabel
            jobTitle
          }
          city
          country
          state
          buyrates {
            buyrateId
            name
            notes
            punchouts {
              payRateMin
              payRateMax
              billRateMin
              billRateMax
              markupPct
            }
          }
          marketRate {
            payRate
            billRate
            markupPct
          }
          rates {
            markupPct {
              min
              max
              avg
              mid
            }
            billRate {
              min
              max
              avg
              mid
            }
            markupAmt {
              min
              max
              avg
              mid
            }
            payRate {
              min
              max
              avg
              mid
            }
          }
          ratesData
          createdDate
        }
      }
        }
        `;

    let res = null;

    this.networkSavedSearchDetail.loading = true;

    try {
      res = await this.fetchGraphQL(query, variables);
    } catch (e) {
      if (axios.isCancel(e)) {
        return e;
      }

      this.networkSavedSearchDetail.handleError("Getting Rate Cards", e);
      if (res !== null) {
        this.networkSavedSearchDetail.logGraphQLError("Get Rate Card query", res);
      }

      // TODO: Display user friendly error message
      return e;
    }

    runInAction("savedSearchDetail--success", () => {
      // this.pagination.goFetch()
      this.networkSavedSearchDetail.loading = false;
      this.networkSavedSearchDetail.error = null;
      if (this.networkSavedSearchDetail.logGraphQLError("Get Rate Cards query", res)) {
        // TODO: Display user friendly error message
        return res;
      }
      this.savedSearchDetailData = res.data.viewer.savedsearch;
      // const savedSearchDetailData = new SavedSearchDetailData(this, data);
    });

    return {
      res: res.data.viewer.savedsearch,
    };
  }
}

export default class DraftsSavedSearchListStore extends DraftsSavedSearchListComponentStore {
  showHelpModal: boolean;
  newRateCardName: string;

  helpModal: ModalState;
  confirmUpdateRatesModal: ModalState;
  confirmUndoUpdateRatesModal: ModalState;
  confirmDeleteModal: ModalState;
  addToRateCardModal: ModalState;
  renameRateCardModal: ModalState;
  networkShareUsers: NetworkState;

  exportModalState: ExportOptionsState;

  rateCardLabel: string;
  uniqueJobLabel: string;
  createRateCardError: string;

  showHelp: () => void;
  hideHelp: () => void;
  showRenameRateCardModal: () => void;
  renameRateCard: () => void;
  onNewRateCardNameChange: (Event) => void;
  deleteRateCards: () => void;
  refreshRateCards: () => void;
  exportRateCards: (options: ExportOptions) => void;
  validateExportOptions: (modalState: ExportOptionsState) => void;
  viewInMap: () => void;
  addSavedSearchToRateCard: () => void;
  createRateCard: () => void;
  changeRateCardLabelText: (Object) => void;
  changeUniqueJobLabelText: (Object) => void;
  fetchGraphQL: FetchGraphQL;
  fetchAPI: FetchAPI;

  constructor(fetchGraphQL: FetchGraphQL, fetchAPI: FetchAPI) {
    super(fetchGraphQL);
    this.fetchGraphQL = fetchGraphQL;
    this.fetchAPI = fetchAPI;

    // Bound early to pass into export state
    this.exportRateCards = action(this.exportRateCards.bind(this));
    this.validateExportOptions = action(this.validateExportOptions.bind(this));

    extendObservable(this, {
      rateCardLabel: "",
      uniqueJobLabel: "",
      createRateCardError: "",
      newRateCardName: "",
      helpModal: new ModalState(),
      renameRateCardModal: new ModalState(),
      confirmUpdateRatesModal: new ModalState(),
      confirmUndoUpdateRatesModal: new ModalState(),
      confirmDeleteModal: new ModalState(),
      addToRateCardModal: new ModalState(),
      exportModal: new ExportOptionsState(
        this.exportRateCards,
        this.validateExportOptions
      ),
    });

    this.showHelp = action(this.showHelp.bind(this));
    this.hideHelp = action(this.hideHelp.bind(this));
    this.showRenameRateCardModal = action(this.showRenameRateCardModal.bind(this));
    this.renameRateCard = action(this.renameRateCard.bind(this));
    this.onNewRateCardNameChange = action(this.onNewRateCardNameChange.bind(this));

    this.deleteRateCards = action(this.deleteRateCards.bind(this));
    this.refreshRateCards = action(this.refreshRateCards.bind(this));
    this.undoLastUpdateRateCards = action(this.undoLastUpdateRateCards.bind(this));
    this.viewInMap = action(this.viewInMap.bind(this));
    this.addSavedSearchToRateCard = action(this.addSavedSearchToRateCard.bind(this));
    this.createRateCard = action(this.createRateCard.bind(this));
    this.changeRateCardLabelText = action(this.changeRateCardLabelText.bind(this));
    this.changeUniqueJobLabelText = action(this.changeUniqueJobLabelText.bind(this));
  }

  showHelp() {
    this.showHelpModal = true;
  }

  hideHelp() {
    this.showHelpModal = false;
  }

  showRenameRateCardModal() {
    const rateCard = this.getFirstSelectedRateCard();
    if (!rateCard) return;

    this.newRateCardName = rateCard.name;
    this.renameRateCardModal.showModal();
  }

  onNewRateCardNameChange(e: Event) {
    this.newRateCardName = sanitizeInput(e.target.value);
  }

  validateExportOptions(modalState: ExportOptionsState) {
    if (modalState.exportOptions.fileType === FILE_TYPE.CSV) {
      modalState.disableExportTypeOptions = true;
      modalState.exportOptions.exportType = EXPORT_TYPE.DETAILED;
      modalState.info = "CSV file format will always include rates data.";
    } else {
      modalState.disableExportTypeOptions = false;
      modalState.info = null;
    }
  }

  exportRateCards(options: ExportOptions) {
    const selectedRateCards = this.getSelectedRateCards();

    if (selectedRateCards.length === 0) {
      return;
    }

    let exportURL: string = "";

    let variables: Object = {
      fileName: options.fileName,
    };

    if (selectedRateCards.length > 1) {
      exportURL = "ratecards/list/export/excel/";
      variables.rateCardFilters = {
        only: selectedRateCards,
      };
    } else {
      // if selectedRateCards.length === 1
      exportURL = [
        "ratecards/",
        selectedRateCards[0],
        "/searches/",
        options.exportType,
        "/list/export/",
        options.fileType,
        "/",
      ].join("");

      variables.searchFilters = {
        exclude: [], // this will include all searches
      };
    }

    this.network.loading = true;

    this.fetchAPI(exportURL, variables)
      .then((res) => res.json())
      .then(
        (data) => {
          window.location = `${"https://api.peopleticker.local:2015"}${
            data.relative_url
          }`;

          this.network.loading = false;
          this.handleStopEdit();
        },
        (err) => {
          this.network.handleError("Exporting Rate Cards", err);
          // TODO: Display user friendly error message
        }
      );
  }

  viewInMap() {
    throw new Error("viewInMap is not implemented");
  }

  async renameRateCard() {
    if (this.network.loading) {
      return;
    }

    const selectedRateCards = this.getSelectedRateCards();
    if (selectedRateCards.length !== 1) {
      console.error("One and only one Rate Card must be selected");
      return;
    }

    const query = `
    mutation updateRateCard($rateCardId: ID!, $name: String!){
      updateRateCards(input: {name: $name, rateCardId: $rateCardId}){
      rateCard{
      ratecardId,
      name,
      createdDate,
      updateDate
      }
      }
      }
    `;
    const variables = {
      rateCardId: selectedRateCards[0],
      name: this.newRateCardName,
    };

    this.network.loading = true;
    let res = null;

    try {
      res = await this.fetchGraphQL(query, variables);
    } catch (e) {
      this.network.handleError("Renaming Rate Card", e);
      // TODO: Display user friendly error message
      return;
    }

    this.renameRateCardModal.hideModal();
    this.handleStopEdit();

    runInAction("renameRateCard--success", () => {
      this.network.loading = false;
      this.network.error = null;
      if (this.network.logGraphQLError("editRateCardName", res)) {
        // TODO: Display user friendly error message
        return;
      }

      this.pagination.goFetch(null);
    });
  }

  async refreshRateCards() {
    if (this.network.loading) {
      return;
    }

    let params = [];
    let args = [];
    let vars = {};

    if (this.allSelected) {
      params.push("$exclude: [ID]!");
      args.push("exclude: $exclude");
      vars.exclude = []; // no exclusions means all will be processed if there are no other filters

      // include current filters
      consolidateAppliedFilters(this.appliedFilters, params, args, vars);
    } else {
      const selectedRateCards = this.getSelectedRateCards();
      if (!selectedRateCards || !selectedRateCards.length) {
        console.error("Cannot update Rate Cards: No Rate Cards selected");
        return;
      }

      params.push("$only: [ID]!");
      args.push("only: $only");
      vars.only = selectedRateCards;
    }

    const query = `
      mutation UpdateRatesOnRateCards(${params.join(", ")}){
        updateRatesOnRateCards(input: {
            clientMutationId: "",
            rateCardFilters: {
              ${args.join(", ")}
            }
          }) {

          success {
            message
          }

          errors {
            __typename
            ...on RateCardNotFoundError {
              message
            }
          }
        }
      }
    `;

    this.confirmUpdateRatesModal.hideModal();
    this.network.loading = true;
    let res = null;

    try {
      res = await this.fetchGraphQL(query, vars);
    } catch (e) {
      this.network.handleError("Updating Rates on Rate Card", e);
      // TODO: Display user friendly error message
      return;
    }

    this.handleStopEdit();

    runInAction("refreshRateCards--success", () => {
      this.network.loading = false;
      this.network.error = null;
      if (this.network.logGraphQLError("updateRatesOnRateCards", res)) {
        // TODO: Display user friendly error message
        return;
      }

      this.pagination.goFetch(null);
    });
  }

  async undoLastUpdateRateCards() {
    if (this.network.loading) {
      return;
    }

    let params = [];
    let args = [];
    let vars = {};

    if (this.allSelected) {
      params.push("$exclude: [ID]!");
      args.push("exclude: $exclude");
      vars.exclude = []; // no exclusions means all will be processed if there are no other filters

      // include current filters
      consolidateAppliedFilters(this.appliedFilters, params, args, vars);
    } else {
      const selectedRateCards = this.getSelectedRateCards();
      if (!selectedRateCards || !selectedRateCards.length) {
        console.error("Cannot update Rate Cards: No Rate Cards selected");
        return;
      }

      params.push("$only: [ID]!");
      args.push("only: $only");
      vars.only = selectedRateCards;
    }

    const query = `
      mutation UndoUpdateRatesOnRateCards(${params.join(", ")}){
        undoUpdateRatesOnRateCards(input: {
            clientMutationId: "",
            rateCardFilters: {
              ${args.join(", ")}
            }
          }) {

          success {
            message
          }

          errors {
            __typename
            ...on RateCardNotFoundError {
              message
            }
          }
        }
      }
    `;

    this.confirmUndoUpdateRatesModal.hideModal();
    this.network.loading = true;
    let res = null;

    try {
      res = await this.fetchGraphQL(query, vars);
    } catch (e) {
      this.network.handleError("Undo last Update Rates on Rate Card", e);
      // TODO: Display user friendly error message
      return;
    }

    this.handleStopEdit();

    runInAction("undoLastUpdateRateCards--success", () => {
      this.network.loading = false;
      this.network.error = null;
      if (this.network.logGraphQLError("undoUpdateRatesOnRateCards", res)) {
        // TODO: Display user friendly error message
        return;
      }

      this.pagination.goFetch(null);
    });
  }

  changeRateCardLabelText(e: Object) {
    this.rateCardLabel = sanitizeInput(e.target.value);
  }

  changeUniqueJobLabelText(e: Object) {
    this.uniqueJobLabel = e.target.value;
  }

  async createRateCard() {
    if (this.network.loading) {
      // NOTE: Debounces createRateCard
      return;
    }

    if (this.rateCardLabel === "") {
      this.messaging.createMessage("info", "Please enter in a label for your ratecard.");
      return;
    }

    if (!validateUserName(this.rateCardLabel)) {
      this.messaging.createMessage(
        "info",
        "Label must start with a letter and contain only letters, spaces, dashes, or numbers."
      );
      return;
    }

    let params = [];
    let filterargs = [];
    let args = [];
    let vars = {};
    let selectedRateCards = [];

    if (this.allSelectedfilter) {
      consolidateAppliedFilters(this.appliedFilters, params, args, vars);
      params.push("$exclude: [String]");
      filterargs.push("exclude: $exclude");
      vars.exclude = this.unSelectedSearches;
      vars.exclude = vars.exclude.filter(function (item, pos) {
        return vars.exclude.indexOf(item) === pos;
      });
    } else {
      selectedRateCards = this.getSelectedRateCards();
      if (!selectedRateCards) selectedRateCards = [];

      params.push("$only: [String]!");
      filterargs.push("only: $only");
      vars.only = selectedRateCards;
    }

    const queryParams = params.join(", ");
    const queryArgs = args.join(", ");

    const saveSearchRateCardNewQuery = `
    	mutation saveSearchRateCardNew($rateCardLabel: String, ${queryParams}) {
    	  saveSearchesToRateCard(input: {newRatecard: $rateCardLabel,${filterargs}, filters: { ${queryArgs} }}) {
    	    searches {
    	      searchId
    	      state
    	      city
    	      createdDate
    	      industry {
    	        value
    	      }
    	    }
          errors{
                __typename
                ...on NameEmptyError{
                  message
                }
                ...on NameAlreadyExistError{
                  message
                }
                ...on TagIdsNotExistsError{
                  message
                }
                ...on NoRateCardError{
                  message
                }
                ...on RateCardIdDoesNotExistError{
                  message
                }
              }
    	  }
    	}`;

    const createRateCardQuery = `
         mutation createRateCard($name: String!) {
          createRateCards(input: {name: $name}){
            rateCard{
              ratecardId,
                owner{
                  firstName,
                  lastName,
                  email,
                  termsOfAgreement
                },
                name,
                createdDate,
                updateDate
              }
              errors{
                __typename
                ...on NameEmptyError{
                  message
                }
                ...on NameAlreadyExistError{
                  message
                }
                ...on TagIdsNotExistsError{
                  message
                }
                ...on NoRateCardError{
                  message
                }
                ...on RateCardIdDoesNotExistError{
                  message
                }
              }
            }
          }
        `;
    const query =
      this.allSelectedfilter || selectedRateCards.length > 0
        ? saveSearchRateCardNewQuery
        : createRateCardQuery;

    const variables =
      (this.allSelectedfilter || selectedRateCards.length > 0) > 0
        ? Object.assign({ rateCardLabel: this.rateCardLabel }, vars)
        : Object.assign({ name: this.rateCardLabel });

    this.network.loading = true;
    let res = null;

    try {
      res = await this.fetchGraphQL(query, variables);
    } catch (e) {
      this.network.handleError("Creating Rate Card", e);
      // TODO: Display user friendly error message
      return;
    }

    this.network.loading = false;

    runInAction("createRateCard--success", () => {
      this.network.loading = false;
      this.network.error = null;
      if (this.network.logGraphQLError("createRateCard", res)) {
        // TODO: Display user friendly error message
        if (!!res.errors && !!res.errors[0]) {
          if (this.messaging.messages.length > 0) {
            for (var i = 0; i < this.messaging.messages.length; i++) {
              this.messaging.removeMessage(this.messaging.messages[i]);
            }
          }
          this.messaging.createMessage("error", res.errors[0].message);
          // this.createRateCardError = res.errors[0].message;
          this.deselectAllPage();
          return;
        }
      }

      if (res.data.createRateCards && res.data.createRateCards.errors) {
        this.messaging.removeAll();
        this.messaging.createMessage("info", res.data.createRateCards.errors[0].message);
        return;
      }
      if (res.data.saveSearchesToRateCard && res.data.saveSearchesToRateCard.errors) {
        this.messaging.removeAll();
        this.messaging.createMessage(
          "info",
          res.data.saveSearchesToRateCard.errors[0].message
        );
        return;
      }
      browserHistory.push("/ratecards/");
    });
  }

  async addSavedSearchToRateCard(existingRateCardId) {
    if (this.network.loading) {
      return;
    }

    let params = [];
    let args = [];
    let vars = {};

    if (this.allSelected) {
      params.push("$exclude: [ID]!");
      args.push("exclude: $exclude");
      vars.exclude = []; // no exclusions means all will be processed if there are no other filters

      // include current filters
      consolidateAppliedFilters(this.appliedFilters, params, args, vars);
    } else {
      const selectedRateCards = this.getSelectedRateCards();
      if (!selectedRateCards || !selectedRateCards.length) {
        console.error("Cannot delete Rate Cards: No Rate Cards selected");
        return;
      }

      params.push("$only: [Int]!");
      args.push("only: $only");
      vars.only = selectedRateCards;
      vars.existingRateCardId = existingRateCardId;
    }

    const query = `
      mutation saveSearchRateCardExisting($only: [Int]!, $existingRateCardId: Int) {
        saveSearchToRateCard(input: {searchId: $only, uniqueJobLabel: "Java Software Engineer", existingRatecard: $existingRateCardId}) {
          searches {
            searchId
            state
            city
            createdDate
            industry {
              value
            }
          }
        }
      }`;

    this.network.loading = true;
    let res = null;

    try {
      res = await this.fetchGraphQL(query, vars);
    } catch (e) {
      this.network.handleError("Deleting selected Rate Cards", e);
      // TODO: Display user friendly error message
      return;
    }

    this.handleStopEdit();

    runInAction("addSavedSearchToRateCard--success", () => {
      this.network.loading = false;
      this.network.error = null;
      if (this.network.logGraphQLError("deleteRateCard", res)) {
        // TODO: Display user friendly error message
        return;
      }

      this.pagination.goFetch(null);
    });
  }

  async deleteRateCards() {
    if (this.network.loading) {
      return;
    }

    let params = [];
    let args = [];
    let vars = {};

    if (this.allSelected) {
      params.push("$exclude: [ID]!");
      args.push("exclude: $exclude");
      vars.exclude = []; // no exclusions means all will be processed if there are no other filters

      // include current filters
      consolidateAppliedFilters(this.appliedFilters, params, args, vars);
    } else {
      const selectedRateCards = this.getSelectedRateCards();
      if (!selectedRateCards || !selectedRateCards.length) {
        console.error("Cannot delete Rate Cards: No Rate Cards selected");
        return;
      }

      params.push("$only: [Int]");
      args.push("only: $only");
      vars.only = selectedRateCards;
    }

    const query = `
      mutation deleteSavedSearch($only: [Int]){
        deleteSavedSearch(input: {searchId: $only}){
          ok
        }
      }
    `;

    this.network.loading = true;
    let res = null;

    try {
      res = await this.fetchGraphQL(query, vars);
    } catch (e) {
      this.network.handleError("Deleting selected Rate Cards", e);
      // TODO: Display user friendly error message
      return;
    }

    this.handleStopEdit();

    runInAction("deleteRateCards--success", () => {
      this.network.loading = false;
      this.network.error = null;
      if (this.network.logGraphQLError("deleteRateCard", res)) {
        // TODO: Display user friendly error message
        return;
      }

      this.pagination.goFetch(null);
    });
  }
}
